import * as React from 'react';

/**
 * Page-composition breakpoint (Tailwind `md`).
 * Stack vs split panes, drawer vs popover, dense toolbars.
 * Do **not** use for browser sidebar reserved-space vs overlay — that is
 * {@link SHELL_SIDEBAR_OVERLAY_BREAKPOINT} / {@link useShellSidebarOverlay}.
 * Do **not** OR Capacitor native into this hook (shell chrome = `isCapacitorNative()`).
 */
export const PAGE_MOBILE_BREAKPOINT = 768;

/**
 * Browser sidebar reserved-space ↔ overlay handoff (Tailwind `xl`).
 *
 * Below this width the shell sidebar is a drawer (no gap). At/above it the
 * floating sidebar reserves horizontal space. Chosen so dense page layouts
 * (Status Board taps|heute, Profile + 31rem preview rail) fit beside an
 * expanded sidebar (~256–360px + float insets) without character-stacking.
 *
 * Keep in sync with `xl:` visibility classes on the desktop sidebar peer in
 * `navigation/sidebar.tsx`.
 */
export const SHELL_SIDEBAR_OVERLAY_BREAKPOINT = 1280;

function readViewportBelow(breakpointPx: number): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < breakpointPx;
}

function useViewportBelow(breakpointPx: number): boolean {
  const [below, setBelow] = React.useState(() =>
    readViewportBelow(breakpointPx),
  );

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpointPx - 1}px)`);
    const onChange = () => {
      setBelow(readViewportBelow(breakpointPx));
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [breakpointPx]);

  return below;
}

/**
 * Viewport-only page mobile breakpoint (`< 768px` / `md`).
 * Do **not** use for shell chrome (sidebar vs bottom tab bar) — that is
 * `isCapacitorNative()` / `shellUsesMobileTabBar()`.
 * Do **not** use for sidebar overlay vs reserved gap — that is
 * {@link useShellSidebarOverlay}.
 */
export function useIsMobile() {
  return useViewportBelow(PAGE_MOBILE_BREAKPOINT);
}

/**
 * True when the browser shell sidebar must be overlay/drawer (no reserved gap).
 * Capacitor never mounts the sidebar; callers still gate with `isCapacitorNative()`.
 */
export function useShellSidebarOverlay() {
  return useViewportBelow(SHELL_SIDEBAR_OVERLAY_BREAKPOINT);
}
